import { createAsyncThunk } from '@reduxjs/toolkit';
import { v4 as uuidv4 } from 'uuid';

import {
  addBucketMedia,
  addRequest,
  addResponse,
  resetAiStudioWithoutRatios,
  selectHistory,
  setBucket,
  setRequestsHistory,
  setResponses,
  setSelectedMediaIndex,
  updateResponse,
} from './aiStudioReducer';
import mediaRuntimeCache from '../../ai-studio/utils/responsesCache';
import sendRequest from '../../service/sendRequest';
import callApi from '../../service/callApi';
import timeout from '../../utils/timeout';
import formatRequest from '../../ai-studio/utils/formatRequest';
import { clearAllRequestFiles, setRequestFiles } from '../../ai-studio/utils/requestFilesCache';
import { STUDIO_API_OPERATION_TYPES } from '../../activ8/studio/constants';
import { buildStudioOperationRequest } from './studioOperationUtils';
import getFileNameFromHeaders from '../../utils/getFileNameFromHeaders';
import { AI_STUDIO_ADAPTERS, AI_STUDIO_SCOPES, AI_STUDIO_STORE_OPERATIONS, CUSTOM_BUCKET_ID } from '../../constants';
import getMimeTypeByMediaType from '../../utils/getMimeTypeByMediaType';
import buildQuery from '../../utils/buildQuery';
import detectType from '../../ai-studio/utils/detectType';
import { clearCaches, mediaCache, setInCache } from '../../service/mediaCache';
import { enqueueSnackbar, somethingWrongNotification } from '../notifier/notifierActions';
import firstLetterToUppercase from '../../utils/firstLetterToUppercase';
import getMediaTypeByFilename from '../../utils/getMediaTypeByFilename';
import decodeErrorMessageFromResponse from '../../utils/decodeErrorMessageFromResponse';
import { clampMediaIndex } from '../../activ8/utils/mediaIndex';

const CHANGE_STUDIO_BUCKET = 'aiStudio/CHANGE_STUDIO_BUCKET';
const CLEAR_STUDIO_REQUESTS_HISTORY = 'aiStudio/CLEAR_STUDIO_REQUESTS_HISTORY';
const CLEAR_STUDIO_RESPONSES = 'aiStudio/CLEAR_STUDIO_RESPONSES';
const CLEAR_ALL_AI_STUDIO = 'aiStudio/CLEAR_ALL_AI_STUDIO';
const GET_FAVORITE_MEDIA = 'aiStudio/GET_FAVORITE_MEDIA';
const REMOVE_FAVORITE_MEDIA = 'aiStudio/REMOVE_FAVORITE_MEDIA';
const ADD_FAVORITE_MEDIA = 'aiStudio/ADD_FAVORITE_MEDIA';
const GET_MEDIA_FROM_SESSION = 'aiStudio/GET_MEDIA_FROM_SESSION';
const GET_IMAGES_RATIO = 'aiStudio/GET_IMAGES_RATIO';
const GET_VIDEO_RATIO = 'aiStudio/GET_FREE_TEXT_IMAGES_RATIO';
const GET_FREE_TEXT_VIDEO_RATIO = 'aiStudio/GET_FREE_TEXT_VIDEO_RATIO';
const ADD_MEDIA = 'aiStudio/ADD_MEDIA';
const SAVE_TO_BUCKET = 'aiStudio/SAVE_TO_BUCKET';
const SAVE_FAVORITE_TO_BUCKET = 'aiStudio/SAVE_FAVORITE_TO_BUCKET';
const REMOVE_BUCKET_ITEM = 'aiStudio/REMOVE_BUCKET_ITEM';
const DOWNLOAD_FAVORITE_MEDIA_BUFFER = 'aiStudio/DOWNLOAD_FAVORITE_MEDIA_BUFFER';
const SET_STUDIO_CUSTOM_BUCKET = 'aiStudio/SET_STUDIO_CUSTOM_BUCKET';
const SET_STUDIO_CUSTOM_BUCKET_FROM_BLOB = 'aiStudio/SET_STUDIO_CUSTOM_BUCKET_FROM_BLOB';
const CREATE_NEW_BUCKET = 'aiStudio/CREATE_NEW_BUCKET';
const CREATE_NEW_BUCKET_FROM_FAVORITE = 'aiStudio/CREATE_NEW_BUCKET_FROM_FAVORITE';

//ACTIONS
const CALL_STUDIO = 'aiStudio/CALL_STUDIO';

export const changeStudioBucket = createAsyncThunk(CHANGE_STUDIO_BUCKET, async ({ bucketId, media }, { dispatch }) => {
  await dispatch(clearStudioRequestsHistory());
  await dispatch(clearStudioResponses());
  clearCaches();

  if (bucketId) {
    dispatch(setBucket({ bucketId, media }));
  } else {
    dispatch(setBucket(null));
  }
});

export const setStudioCustomBucket = createAsyncThunk(SET_STUDIO_CUSTOM_BUCKET, async ({ file }, { dispatch }) => {
  const bucketId = CUSTOM_BUCKET_ID;
  const id = uuidv4();
  const fileName = file.name;
  const arrayBuffer = await file.arrayBuffer();
  const createdMedia = mediaRuntimeCache.create(arrayBuffer, {
    id,
    fileName,
    contentType: file.type,
  });
  setInCache(mediaCache, id, createdMedia.previewUrl);

  const media = [
    {
      id: id,
      mediaType: detectType(file),

      isCustom: true,

      fileName: fileName,
    },
  ];

  dispatch(setBucket({ bucketId, media }));
});

export const downloadFavoriteMediaBuffer = createAsyncThunk(
  DOWNLOAD_FAVORITE_MEDIA_BUFFER,
  async ({ id }, { rejectWithValue }) => {
    const response = await sendRequest(
      () => callApi.get(`/api/studio/favorite/download?id=${id}`, { responseType: 'arraybuffer' }),
      { onError: (e) => rejectWithValue(e) },
    );

    return {
      arrayBuffer: response.data,
      fileName: getFileNameFromHeaders(response.headers),
    };
  },
);

export const setStudioCustomBucketFromBlob = createAsyncThunk(
  SET_STUDIO_CUSTOM_BUCKET_FROM_BLOB,
  async ({ arrayBuffer, fileName, mediaType }, { dispatch }) => {
    await dispatch(clearStudioRequestsHistory());
    await dispatch(clearStudioResponses());
    clearCaches();

    const bucketId = CUSTOM_BUCKET_ID;
    const id = uuidv4();
    const contentType = getMimeTypeByMediaType(mediaType);
    const createdMedia = mediaRuntimeCache.create(arrayBuffer, {
      id,
      fileName,
      contentType,
    });
    setInCache(mediaCache, id, createdMedia.previewUrl);

    const media = [
      {
        id,
        mediaType,
        isCustom: true,
        fileName,
        mimeType: contentType,
      },
    ];

    dispatch(setBucket({ bucketId, media }));
    dispatch(setSelectedMediaIndex(0));
  },
);

export const clearStudioResponses = createAsyncThunk(CLEAR_STUDIO_RESPONSES, async (_, { dispatch }) => {
  mediaRuntimeCache.clear();
  dispatch(setResponses([]));
});

export const clearStudioRequestsHistory = createAsyncThunk(
  CLEAR_STUDIO_REQUESTS_HISTORY,
  async (_, { dispatch, getState }) => {
    const requestsHistory = getState().aiStudio.requestsHistory;

    if (requestsHistory.length) {
      requestsHistory.forEach((request) => {
        request.attachments.forEach((attachment) => {
          if (attachment.thumbnailUrl) {
            URL.revokeObjectURL(attachment.thumbnailUrl);
          }
        });
      });
    }

    clearAllRequestFiles();
    dispatch(setRequestsHistory([]));
  },
);

export const clearAllAiStudio = createAsyncThunk(CLEAR_ALL_AI_STUDIO, async (_, { dispatch }) => {
  await dispatch(clearStudioRequestsHistory());
  await dispatch(clearStudioResponses());
  await dispatch(changeStudioBucket({ bucketId: null, media: null }));
  dispatch(resetAiStudioWithoutRatios());
});

export const saveToBucket = createAsyncThunk(
  SAVE_TO_BUCKET,
  async ({ id, mediaType, fileName }, { dispatch, getState, rejectWithValue }) => {
    const bucket = getState().aiStudio.bucket;
    const selectedMediaIndex = getState().aiStudio.selectedMediaIndex;
    const selectedMedia = bucket.media[selectedMediaIndex];

    const mimeType = getMimeTypeByMediaType(mediaType);
    const file = new File([mediaRuntimeCache.getBlob(id)], fileName, { type: mimeType });

    if (!selectedMedia || !file) {
      return rejectWithValue('No file found');
    }

    const formData = new FormData();
    formData.append('file', file, fileName);

    const query = buildQuery({
      context: AI_STUDIO_ADAPTERS.ACTIV8,
      contentId: selectedMedia.id,
      operation: AI_STUDIO_STORE_OPERATIONS.ADD_TO_BUCKET,
    });

    const response = await sendRequest(() =>
      callApi.post(`/api/studio/favorite/store?${query}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      }),
    );

    dispatch(updateResponse({ id, newContent: { isSaved: true } }));
    dispatch(addBucketMedia(response.data));
  },
);

export const saveFavoriteToBucket = createAsyncThunk(
  SAVE_FAVORITE_TO_BUCKET,
  async ({ id, adapter, projectId }, { dispatch }) => {
    const query = buildQuery({
      context: adapter,
      favoriteId: id,
      projectId,
      operation: AI_STUDIO_STORE_OPERATIONS.ADD_TO_BUCKET,
    });

    await sendRequest(() => callApi.post(`/api/studio/favorite/store?${query}`), {
      onSuccess: () => {
        dispatch(
          enqueueSnackbar({
            message: 'mediaHasBeenAddedToTheSourceBucketSuccessfully',
            options: {
              variant: 'success',
            },
          }),
        );
      },
    });

    dispatch(updateResponse({ id, newContent: { isSaved: true } }));

    await timeout(300);
    dispatch(getFavoriteMedia({ projectId }));
  },
);

export const getFavoriteMedia = createAsyncThunk(
  GET_FAVORITE_MEDIA,
  async ({ projectId }, { getState, rejectWithValue }) => {
    const favoriteTableSettings = getState().aiStudio.favoriteTableSettings;

    const query = buildQuery({
      page: favoriteTableSettings.page + 1,
      size: favoriteTableSettings.size,
      scope: AI_STUDIO_SCOPES.PROJECT,
      projectId,
      context: AI_STUDIO_ADAPTERS.ACTIV8,
    });

    const response = await sendRequest(() => callApi.get(`/api/studio/favorite/search?${query}`), {
      onError: (e) => {
        return rejectWithValue(e);
      },
    });

    const formattedResponse = response.data.content.map((item) => {
      return {
        id: item.id,
        ...item,
      };
    });

    return { content: formattedResponse, totalElements: response.data.totalElements };
  },
);

export const removeFavoriteMedia = createAsyncThunk(REMOVE_FAVORITE_MEDIA, async ({ id, projectId }, { dispatch }) => {
  sendRequest(() => callApi.delete(`/api/studio/favorite/delete?id=${id}`));

  await timeout(300);
  dispatch(getFavoriteMedia({ projectId }));

  return { id };
});

export const addFavoriteMedia = createAsyncThunk(
  ADD_FAVORITE_MEDIA,
  async ({ fileName, mediaType, id, projectId }, { dispatch, getState, rejectWithValue }) => {
    const selectedMediaIndex = getState().aiStudio.selectedMediaIndex;
    const bucket = getState().aiStudio.bucket;
    const selectedMedia = bucket.media[selectedMediaIndex];

    const mimeType = getMimeTypeByMediaType(mediaType);
    const file = new File([mediaRuntimeCache.getBlob(id)], fileName, { type: mimeType });

    const formData = new FormData();
    formData.append('attached', file, fileName);

    const query = buildQuery({
      scope: AI_STUDIO_SCOPES.PROJECT,
      context: AI_STUDIO_ADAPTERS.ACTIV8,
      projectId,

      ...(bucket.bucketId !== CUSTOM_BUCKET_ID ? { sourceContentId: selectedMedia.id } : {}),
    });

    const response = await sendRequest(() =>
      callApi.post(`/api/studio/favorite/save?${query}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      }),
    );

    return { id, favoriteId: response.data };
  },
);

export const getMediaFromSession = createAsyncThunk(
  GET_MEDIA_FROM_SESSION,
  async ({ sessionId }, { rejectWithValue }) => {
    const response = await sendRequest(
      () => callApi.get(`/api/studio/operations/session?sessionId=${sessionId}`, { responseType: 'arraybuffer' }),
      {
        onError: (e) => {
          return rejectWithValue(e);
        },
      },
    );

    return response;
  },
);

export const addMedia = createAsyncThunk(
  ADD_MEDIA,
  async ({ request, response, contentType }, { rejectWithValue, dispatch, getState }) => {
    const fileName = getFileNameFromHeaders(response.headers);
    const mediaType = getMediaTypeByFilename(fileName);
    const detectedContentType = contentType || getMimeTypeByMediaType(mediaType);

    const createdMedia = mediaRuntimeCache.create(response.data, {
      requestId: request.id,
      time: request.time,
      actionType: request.actionType,
      contentType: detectedContentType,
      mediaType: mediaType,
      fileName: fileName ?? request.id,
    });

    dispatch(addResponse(createdMedia));
  },
);

export const removeBucketItem = createAsyncThunk(REMOVE_BUCKET_ITEM, async ({ id }, { dispatch, getState }) => {
  const bucket = getState().aiStudio.bucket;
  const selectedMediaIndex = getState().aiStudio.selectedMediaIndex;
  const updatedMedia = bucket.media.filter((mediaItem) => mediaItem.id !== id);

  if (updatedMedia.length === 0) {
    await dispatch(clearAllAiStudio());
    return null;
  }

  dispatch(setBucket({ ...bucket, media: updatedMedia }));

  if (selectedMediaIndex >= updatedMedia.length) {
    dispatch(setSelectedMediaIndex(clampMediaIndex(selectedMediaIndex, updatedMedia.length)));
  }

  return null;
});

export const createNewBucketFromFavorite = createAsyncThunk(
  CREATE_NEW_BUCKET_FROM_FAVORITE,
  async ({ projectId, id }, { dispatch }) => {
    const query = buildQuery({
      scope: AI_STUDIO_SCOPES.PROJECT,
      context: AI_STUDIO_ADAPTERS.ACTIV8,
      favoriteId: id,
      projectId,
      operation: AI_STUDIO_STORE_OPERATIONS.NEW_CONTENT,
    });

    const response = await sendRequest(() => callApi.post(`/api/studio/favorite/store?${query}`), {
      onSuccess: () => {
        dispatch(
          enqueueSnackbar({
            message: 'bucketHasBeenCreatedSuccessfully',
            options: {
              variant: 'success',
            },
          }),
        );
      },
    });

    // dispatch(changeStudioBucket({ bucketId: response.data.bucketId, media: response.data.media }));
  },
);

export const createNewBucket = createAsyncThunk(CREATE_NEW_BUCKET, async ({ projectId, file }, { dispatch }) => {
  const query = buildQuery({
    context: AI_STUDIO_ADAPTERS.ACTIV8,
    projectId,
    operation: AI_STUDIO_STORE_OPERATIONS.NEW_CONTENT,
  });

  const formData = new FormData();
  formData.append('file', file);

  await sendRequest(
    () =>
      callApi.post(`/api/studio/favorite/store?${query}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      }),
    {
      onSuccess: () => {
        dispatch(
          enqueueSnackbar({
            message: 'bucketHasBeenCreatedSuccessfully',
            options: {
              variant: 'success',
            },
          }),
        );
      },
    },
  );
});

// AI STUDIO ACTIONS
export const callAiStudio = createAsyncThunk(
  CALL_STUDIO,
  async ({ values, adapter, dropdownOptions }, { rejectWithValue, dispatch, getState }) => {
    const { message, attachments, action, file, ...fieldValues } = values;

    const bucket = getState().aiStudio.bucket;
    const selectedMedia = bucket?.media?.[getState().aiStudio.selectedMediaIndex];

    if (!selectedMedia) {
      return rejectWithValue('No media selected');
    }

    const chatAttachmentFiles = (attachments ?? []).map((attachment) => attachment.file).filter(Boolean);

    const request = await formatRequest({
      attachments: [file, ...chatAttachmentFiles],
      actionType: action,
      fieldValues: { ...fieldValues, message: message ?? '' },
      dropdownOptions,
    });

    setRequestFiles(request.id, {
      attachments: chatAttachmentFiles,
      file: file || null,
    });

    try {
      const requestBody = await buildStudioOperationRequest({
        values,
        adapter,
        bucket,
        selectedMedia,
        mediaType: selectedMedia.mediaType,
        dropdownOptions,
        onSaveFiles: saveFiles,
      });

      const isRecreate = requestBody.operationType === STUDIO_API_OPERATION_TYPES.RECREATE;

      const response = await aiStudioAction({
        requestBody,
        isRecreate,
        dispatch,
        rejectWithValue,
      });

      if (isRecreate) {
        const sessionIds = response.data.map((item) => item.sessionId);

        for (const sessionId of sessionIds) {
          const mediaResponse = await dispatch(getMediaFromSession({ sessionId }));
          dispatch(addMedia({ request, response: mediaResponse.payload }));
        }
      } else {
        dispatch(addMedia({ request, response }));
      }
    } catch (error) {
      return rejectWithValue(error);
    }

    dispatch(addRequest(request));
    dispatch(selectHistory(request.id));
  },
);

export const saveFiles = async ({ files }) => {
  const formData = new FormData();
  files.forEach((file) => formData.append('files', file));

  const response = await sendRequest(() =>
    callApi.post('/api/file/content/temp', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  );

  return response.data;
};

export const aiStudioAction = async ({ requestBody, isRecreate, dispatch, rejectWithValue }) => {
  const response = await sendRequest(
    () =>
      callApi.post('/api/studio/operations/operation', requestBody, {
        ...(isRecreate ? {} : { responseType: 'arraybuffer' }),
      }),
    {
      onError: (e) => {
        const status = e.response?.status ?? e.status;
        const message = decodeErrorMessageFromResponse(e);

        if (status !== 500) {
          dispatch(enqueueSnackbar({ message, options: { variant: 'error' } }));
        } else {
          dispatch(somethingWrongNotification());
        }
        return rejectWithValue(e);
      },
    },
  );

  return response;
};

export const getImagesRatio = createAsyncThunk(GET_IMAGES_RATIO, async (_, { rejectWithValue, getState }) => {
  const imagesRatio = getState().aiStudio.imagesRatio;

  if (imagesRatio.length) {
    return imagesRatio;
  }

  const response = await sendRequest(() => callApi.get('/api/studio/operations/ratio/image/ratios'), {
    onError: (e) => {
      return rejectWithValue(e);
    },
  });

  const formattedRatioOptions = Object.entries(response?.data ?? {}).map(([key, value]) => {
    return {
      label: firstLetterToUppercase(key),
      value: value,
    };
  });

  return formattedRatioOptions;
});

export const getVideoRatio = createAsyncThunk(GET_VIDEO_RATIO, async (_, { rejectWithValue, getState }) => {
  const videoRatio = getState().aiStudio.videoRatio;

  if (videoRatio.length) {
    return videoRatio;
  }

  const response = await sendRequest(() => callApi.get('/api/studio/operations/ratio/video/ratios'), {
    onError: (e) => {
      return rejectWithValue(e);
    },
  });

  const formattedRatioOptions = Object.entries(response?.data ?? {}).map(([key, value]) => {
    return {
      label: firstLetterToUppercase(key),
      value: value,
    };
  });

  return formattedRatioOptions;
});

export const getFreeTextVideoRatio = createAsyncThunk(
  GET_FREE_TEXT_VIDEO_RATIO,
  async (_, { rejectWithValue, getState }) => {
    const freeTextVideoRatio = getState().aiStudio.freeTextVideoRatio;

    if (freeTextVideoRatio.length) {
      return freeTextVideoRatio;
    }

    const response = await sendRequest(() => callApi.get('/api/studio/operations/free/text/video/ratios'), {
      onError: (e) => {
        return rejectWithValue(e);
      },
    });

    const formattedRatioOptions = Object.entries(response?.data ?? {}).map(([key, value]) => {
      return {
        label: firstLetterToUppercase(key),
        value: value,
      };
    });

    return formattedRatioOptions;
  },
);
